'use client';

import { useState, useEffect, useCallback } from 'react';
import { styled, keyframes } from 'styled-components';
import * as Styled from '@lib/styles';
import Link from 'next/link';
import Button from '@dvd/components/Button';
import {
  getStoredConsent,
  updateConsent,
  isBrowser,
  DEFAULT_CONSENT_SETTINGS,
  ACCEPT_ALL_SETTINGS,
} from '@lib/gtm';

// Delay before showing the consent banner (ms) to prevent flicker
// const CONSENT_BANNER_DELAY = 300;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const BannerContainer = styled.div`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing[1.5]};
  right: ${({ theme }) => theme.spacing[1.5]};
  background-color: ${({ theme }) => theme.colors.blue800};
  border-radius: ${({ theme }) => theme.radius.std};
  box-shadow: 0 0 1px 0 ${({ theme }) => theme.colors.blue200};
  color: ${({ theme }) => theme.colors.white};
  max-width: 340px; // Limit per design
  padding: ${({ theme }) => theme.spacing[1.5]};
  z-index: 9999;
  animation: ${fadeIn} 111ms ease-in-out forwards;

  // Using just a next/link here
  && a {
    color: ${({ theme }) => theme.colors.white};
  }
`;

// Override global h5 margin to match design
const BannerHeadline = styled.h5`
  margin-bottom: ${({ theme }) => theme.spacing[0.5]};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
`;

// Override mini font size to match design
const ConsentButton = styled(Button)`
  && span {
    font-size: ${({ theme }) => theme.typography.fontSize.bodySm};
  }
`;

/**
 * Shows cookie consent options to users, when no consent is stored.
 */
const ConsentBanner = () => {
  // Always start hidden to avoid hydration mismatches
  const [showBanner, setShowBanner] = useState(false);

  // Initialize consent and check banner state
  useEffect(() => {
    // Safety check for client-side execution
    if (!isBrowser()) return;

    if (!getStoredConsent()) {
      // TODO: Refactor to initialize state from getStoredConsent() directly instead of using useEffect
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowBanner(true);
    }
  }, []);

  // Accept all cookies
  const handleAcceptAll = useCallback(() => {
    updateConsent(ACCEPT_ALL_SETTINGS);
    setShowBanner(false);
  }, []);

  // Reject optional cookies
  const handleRejectAll = useCallback(() => {
    updateConsent(DEFAULT_CONSENT_SETTINGS);
    setShowBanner(false);
  }, []);

  if (!showBanner) return null;

  return (
    <BannerContainer>
      <BannerHeadline>We value your privacy</BannerHeadline>
      <Styled.BodyXs>
        We use cookies to enhance your browsing experience, enable core site
        functionality, analyze our traffic, and serve personalized ads or
        content. By clicking “Accept All” you consent to our use of cookies. For
        more information, please see the full{' '}
        <Link href="/privacy">Privacy Policy</Link>.
      </Styled.BodyXs>
      <ButtonContainer>
        <ConsentButton
          variant="primary"
          color="blue500"
          onClick={handleAcceptAll}
          size="mini"
        >
          Accept All
        </ConsentButton>
        <ConsentButton
          variant="secondary"
          onClick={handleRejectAll}
          color="white"
          size="mini"
        >
          Reject Optional
        </ConsentButton>
      </ButtonContainer>
    </BannerContainer>
  );
};

// Export banner as client-only component to prevent hydration mismatches
export default ConsentBanner;
